/**
 * @fileoverview Meta Ads integration content for the Integrations page.
 * @module meta-ads-connect/components/MetaAdsIntegrationContent
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '@frontend/contexts/AuthContext';
import { CronTasksAdminApiClient } from '@modules/content-plan/services/CronTasksAdminApiClient';
import { useMetaAdsConnect } from '../hooks/useMetaAdsConnect';
import { MetaAdsCronScheduleService } from '../services/MetaAdsCronScheduleService';
import { MetaAdsUtmMappingSection } from './MetaAdsUtmMappingSection';
import { META_ADS_ACCOUNT_SELECTION_TEST_IDS } from '../testIds';
import type { MetaAdsAdAccountSummary } from '../types';
import type { MetaAdsConnectService } from '../services/MetaAdsConnectService';

export interface MetaAdsIntegrationContentProps {
  projectId: string;
  showHeader?: boolean;
  /** Test seam (Google Ads parity): overrides the HTTP connect service. */
  service?: Pick<
    MetaAdsConnectService,
    | 'getOAuthUrl' | 'getStatus' | 'disconnect' | 'listAdAccounts'
    | 'getInsightsPreview' | 'getMetadataPreview' | 'syncDelivery'
    | 'syncMetadata' | 'selectAdAccounts'
  >;
}

/** Selection cap mirrors the server-side bound (1..100). */
const MAX_SELECTED_AD_ACCOUNTS = 100;

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function toDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getDefaultDateRange(): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until);
  since.setDate(since.getDate() - 6);
  return {
    since: toDateInput(since),
    until: toDateInput(until),
  };
}

function formatAccountLabel(account: MetaAdsAdAccountSummary): string {
  const details = [account.currency, account.timezoneName].filter(Boolean).join(' | ');
  const base = account.name || account.accountId || account.id;
  return details ? `${base} (${details})` : base;
}

function getRowValue(row: Record<string, unknown>, key: string): string {
  const value = row[key];
  if (value === undefined || value === null) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function getOptionalRowValue(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  if (typeof value !== 'string' || !value.trim()) return null;
  return value.trim();
}

function getNestedId(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const nestedId = (value as Record<string, unknown>).id;
  return typeof nestedId === 'string' && nestedId.trim() ? nestedId.trim() : null;
}

function formatUtmValues(row: Record<string, unknown>): string | null {
  const value = row.extractedUtm;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const utm = value as Record<string, unknown>;
  const pairs = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id']
    .map((key) => {
      const item = utm[key];
      return typeof item === 'string' && item.trim() ? `${key}=${item.trim()}` : null;
    })
    .filter((item): item is string => Boolean(item));
  return pairs.length > 0 ? pairs.join(' | ') : null;
}

function formatMetadataRowLines(label: string, row: Record<string, unknown>): string[] {
  const lines = [
    `${getRowValue(row, 'name')} | ${getRowValue(row, 'id')} | status ${getRowValue(row, 'effective_status')}`,
  ];

  if (label === 'Ad sets') {
    const campaignId = getOptionalRowValue(row, 'campaign_id');
    if (campaignId) lines.push(`campaign_id ${campaignId}`);
  }

  if (label === 'Ads') {
    const details = [
      getOptionalRowValue(row, 'campaign_id') ? `campaign_id ${getOptionalRowValue(row, 'campaign_id')}` : null,
      getOptionalRowValue(row, 'adset_id') ? `adset_id ${getOptionalRowValue(row, 'adset_id')}` : null,
      getNestedId(row, 'creative') ? `creative_id ${getNestedId(row, 'creative')}` : null,
    ].filter((item): item is string => Boolean(item));
    if (details.length > 0) lines.push(details.join(' | '));
  }

  if (label === 'Creatives') {
    const destinationUrl = getOptionalRowValue(row, 'primaryDestinationUrl')
      || getOptionalRowValue(row, 'link_url')
      || getOptionalRowValue(row, 'object_url');
    const normalizedDestinationUrl = getOptionalRowValue(row, 'normalizedDestinationUrl');
    const urlTags = getOptionalRowValue(row, 'url_tags');
    const utm = formatUtmValues(row);

    if (destinationUrl) lines.push(`destination ${destinationUrl}`);
    if (normalizedDestinationUrl && normalizedDestinationUrl !== destinationUrl) {
      lines.push(`normalized_destination ${normalizedDestinationUrl}`);
    }
    if (urlTags) lines.push(`url_tags ${urlTags}`);
    if (utm) lines.push(`utm ${utm}`);
  }

  return lines;
}

export function MetaAdsIntegrationContent({
  projectId,
  showHeader = false,
  service,
}: MetaAdsIntegrationContentProps) {
  const theme = useTheme();
  const { currentUser, isAdmin } = useAuth() as {
    currentUser: { uid: string; getIdToken: () => Promise<string> } | null;
    isAdmin?: boolean;
  };
  const {
    status,
    adAccounts,
    preview,
    metadataPreview,
    syncResult,
    metadataSyncResult,
    isConnected,
    loading,
    actionLoading,
    accountsLoading,
    previewLoading,
    metadataPreviewLoading,
    syncLoading,
    metadataSyncLoading,
    error,
    connect,
    disconnect,
    refreshStatus,
    refreshAdAccounts,
    loadInsightsPreview,
    loadMetadataPreview,
    syncDelivery,
    syncMetadata,
    clearPreview,
    saveAdAccountSelection,
    selectionSaving,
  } = useMetaAdsConnect(projectId, service);

  const defaultDateRange = useMemo(getDefaultDateRange, []);
  const cronScheduleService = useMemo(
    () =>
      new MetaAdsCronScheduleService(
        new CronTasksAdminApiClient({
          getAuthToken: async () => (currentUser ? currentUser.getIdToken() : null),
        }),
      ),
    [currentUser],
  );
  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');
  const [manualAdAccountId, setManualAdAccountId] = useState('');
  // G1 (SI-7): explicit sync/report selection — draft mirrors the persisted
  // list until the user edits; `null` = no local edits.
  const [draftSelection, setDraftSelection] = useState<string[] | null>(null);
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [since, setSince] = useState(defaultDateRange.since);
  const [until, setUntil] = useState(defaultDateRange.until);
  const [metadataNamePrefix, setMetadataNamePrefix] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState<'metadata' | 'insights' | null>(null);
  const [scheduleNotice, setScheduleNotice] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedAdAccountId && adAccounts[0]) {
      setSelectedAdAccountId(adAccounts[0].id);
    }
  }, [adAccounts, selectedAdAccountId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const connectedAt = formatDate(status?.connectedAt);
  const expiresAt = formatDate(status?.accessTokenExpiresAt);
  const providerConfigured = status?.providerConfigured ?? false;
  const connectionReady = Boolean(isConnected && !status?.needsReconnect);
  const previewAdAccountId = selectedAdAccountId || manualAdAccountId.trim();
  const selectedAdAccount = adAccounts.find((account) => account.id === previewAdAccountId);
  const selectedAdAccountLabel = selectedAdAccount ? formatAccountLabel(selectedAdAccount) : previewAdAccountId;
  const canPreview = Boolean(connectionReady && previewAdAccountId && since && until && !previewLoading);
  const canPreviewMetadata = Boolean(connectionReady && previewAdAccountId && !metadataPreviewLoading);
  const canSyncDelivery = Boolean(connectionReady && previewAdAccountId && since && until && !syncLoading);
  const canSyncMetadata = Boolean(connectionReady && previewAdAccountId && !metadataSyncLoading);
  const canScheduleSync = Boolean(isAdmin && currentUser && connectionReady && previewAdAccountId && !scheduleLoading);

  const handlePreview = async () => {
    if (!previewAdAccountId) return;
    await loadInsightsPreview({
      adAccountId: previewAdAccountId,
      since,
      until,
      limit: 10,
    });
  };

  const handleMetadataPreview = async () => {
    if (!previewAdAccountId) return;
    await loadMetadataPreview({
      adAccountId: previewAdAccountId,
      limit: 10,
      maxPages: 3,
      namePrefix: metadataNamePrefix.trim() || undefined,
    });
  };

  const handleSyncDelivery = async () => {
    if (!previewAdAccountId) return;
    await syncDelivery({
      adAccountId: previewAdAccountId,
      since,
      until,
      limit: 100,
    });
  };

  const handleSyncMetadata = async () => {
    if (!previewAdAccountId) return;
    // Page 50 avoids Meta transient 500s on heavy /ads listings; 20 pages
    // keeps the 1000-objects-per-type coverage.
    await syncMetadata({
      adAccountId: previewAdAccountId,
      limit: 50,
      maxPages: 20,
      namePrefix: metadataNamePrefix.trim() || undefined,
    });
  };

  const handleScheduleMetadata = async () => {
    if (!currentUser || !previewAdAccountId) return;
    setScheduleLoading('metadata');
    setScheduleNotice(null);
    setScheduleError(null);
    try {
      const result = await cronScheduleService.upsertMetadataSync({
        projectId,
        userId: currentUser.uid,
        adAccountId: previewAdAccountId,
        adAccountLabel: selectedAdAccountLabel,
        namePrefix: metadataNamePrefix.trim() || undefined,
      });
      setScheduleNotice(`${result.created ? 'Created' : 'Updated'} daily metadata sync task ${result.task.id}.`);
    } catch (err) {
      setScheduleError(err instanceof Error ? err.message : 'Failed to schedule Meta Ads metadata sync');
    } finally {
      setScheduleLoading(null);
    }
  };

  const handleScheduleInsights = async () => {
    if (!currentUser || !previewAdAccountId) return;
    setScheduleLoading('insights');
    setScheduleNotice(null);
    setScheduleError(null);
    try {
      const result = await cronScheduleService.upsertInsightsSync({
        projectId,
        userId: currentUser.uid,
        adAccountId: previewAdAccountId,
        adAccountLabel: selectedAdAccountLabel,
      });
      // Lifetime + diagnostics sampling rides the same scheduling click:
      // the diagnostics feed is non-reconstructible, so it must start the
      // moment an account begins syncing insights.
      const lifetime = await cronScheduleService.upsertLifetimeSync({
        projectId,
        userId: currentUser.uid,
        adAccountId: previewAdAccountId,
        adAccountLabel: selectedAdAccountLabel,
      });
      setScheduleNotice(
        `${result.created ? 'Created' : 'Updated'} hourly insights sync task ${result.task.id}; `
        + `${lifetime.created ? 'created' : 'updated'} daily lifetime sync task ${lifetime.task.id}.`,
      );
    } catch (err) {
      setScheduleError(err instanceof Error ? err.message : 'Failed to schedule Meta Ads insights sync');
    } finally {
      setScheduleLoading(null);
    }
  };

  return (
    <Box>
      {showHeader && (
        <>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Meta Ads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect Meta Marketing API access before enabling paid acquisition analytics.
          </Typography>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!providerConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Provider credentials required</AlertTitle>
          Configure the Meta App ID, App Secret, callback URL, and Graph API version in Admin before connecting.
        </Alert>
      )}

      {isConnected ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {status?.needsReconnect && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Reconnect required</AlertTitle>
              {status.lastConnectionError || 'The stored Meta Ads authorization can no longer be used.'}
              {' '}Reconnect to replace only Segmently's stored authorization. This does not change ads in Meta.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <CampaignIcon color="action" sx={{ fontSize: 40 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {status?.displayName ? `Connected as ${status.displayName}` : 'Meta Ads connected'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status?.metaUserId ? `Meta user ID: ${status.metaUserId}` : 'Marketing API access is authorized'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {(status?.scopes || []).map((scope) => (
                  <Chip key={scope} size="small" label={scope} variant="outlined" />
                ))}
                {status?.graphApiVersion && (
                  <Chip size="small" label={`Graph ${status.graphApiVersion}`} color="primary" variant="outlined" />
                )}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              {status?.needsReconnect && (
                <Button
                  variant="contained"
                  startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <CampaignIcon />}
                  onClick={connect}
                  disabled={actionLoading || !providerConfigured}
                >
                  {actionLoading ? 'Connecting…' : 'Reconnect Meta Ads'}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshStatus}
                disabled={actionLoading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <LinkOffIcon />}
                onClick={disconnect}
                disabled={actionLoading}
              >
                Disconnect
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
            {connectedAt && (
              <Typography variant="body2" color="text.secondary">
                Connected: {connectedAt}
              </Typography>
            )}
            {expiresAt && (
              <Typography variant="body2" color="text.secondary">
                Access token expires: {expiresAt}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                Accessible ad accounts
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={accountsLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={refreshAdAccounts}
                disabled={accountsLoading || actionLoading || !connectionReady}
              >
                Refresh Accounts
              </Button>
            </Stack>

            {adAccounts.length > 0 ? (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                {adAccounts.map((account) => (
                  <Chip
                    key={account.id}
                    label={formatAccountLabel(account)}
                    variant={selectedAdAccountId === account.id ? 'filled' : 'outlined'}
                    color={selectedAdAccountId === account.id ? 'primary' : 'default'}
                    onClick={() => {
                      setSelectedAdAccountId(account.id);
                      setManualAdAccountId('');
                      clearPreview();
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                No ad accounts are cached yet. Refresh accounts after connecting, or enter an ad account ID manually for preview.
              </Alert>
            )}
          </Box>

          {adAccounts.length > 0 && (() => {
            // G1 (SI-7): explicit selection surface — only checked accounts
            // are synced and reported; an empty selection means NO accounts.
            const persistedSelection = [...(status?.selectedAdAccountIds ?? [])].sort();
            const effectiveSelection = draftSelection ?? persistedSelection;
            const effectiveSet = new Set(effectiveSelection);
            const selectionDirty = draftSelection !== null
              && JSON.stringify([...draftSelection].sort()) !== JSON.stringify(persistedSelection);
            const toggleAccount = (accountId: string) => {
              setSelectionNotice(null);
              setSelectionError(null);
              setDraftSelection(
                effectiveSet.has(accountId)
                  ? effectiveSelection.filter((id) => id !== accountId)
                  : [...effectiveSelection, accountId],
              );
            };
            const handleSaveSelection = async () => {
              setSelectionNotice(null);
              setSelectionError(null);
              try {
                const result = await saveAdAccountSelection(effectiveSelection);
                setDraftSelection(null);
                setSelectionNotice(`Saved ${result.selectedAdAccountIds.length} Meta ad account${result.selectedAdAccountIds.length === 1 ? '' : 's'}. Sync tasks are provisioned automatically.`);
              } catch (saveError) {
                setSelectionError(saveError instanceof Error ? saveError.message : 'Failed to save the account selection');
              }
            };
            const handleDiscardSelection = () => {
              setDraftSelection(null);
              setSelectionNotice(null);
              setSelectionError(null);
            };
            return (
              <Box sx={{ mt: 3 }} data-testid={META_ADS_ACCOUNT_SELECTION_TEST_IDS.section}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    Sync &amp; report account selection
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    data-testid={META_ADS_ACCOUNT_SELECTION_TEST_IDS.selectedCount}
                    label={`${effectiveSelection.length} selected`}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Only checked accounts are synced to the warehouse and included in paid-acquisition
                  reports. Unchecked accounts stop syncing (their history is kept). An empty
                  selection means no accounts.
                </Typography>
                <FormGroup sx={{ mt: 1 }}>
                  {adAccounts.map((account) => {
                    const checked = effectiveSet.has(account.id);
                    return (
                      <FormControlLabel
                        key={account.id}
                        control={(
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={!connectionReady
                              || selectionSaving
                              || (!checked && effectiveSelection.length >= MAX_SELECTED_AD_ACCOUNTS)}
                            onChange={() => toggleAccount(account.id)}
                            inputProps={{
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              'data-testid': META_ADS_ACCOUNT_SELECTION_TEST_IDS.checkbox(account.id),
                            } as any}
                          />
                        )}
                        label={formatAccountLabel(account)}
                      />
                    );
                  })}
                </FormGroup>
                {selectionNotice && (
                  <Alert severity="success" sx={{ mt: 1 }} data-testid={META_ADS_ACCOUNT_SELECTION_TEST_IDS.notice}>
                    {selectionNotice}
                  </Alert>
                )}
                {selectionError && (
                  <Alert severity="error" sx={{ mt: 1 }} data-testid={META_ADS_ACCOUNT_SELECTION_TEST_IDS.error}>
                    {selectionError}
                  </Alert>
                )}
                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    data-testid={META_ADS_ACCOUNT_SELECTION_TEST_IDS.saveButton}
                    disabled={!connectionReady || !selectionDirty || selectionSaving || effectiveSelection.length === 0}
                    startIcon={selectionSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    onClick={handleSaveSelection}
                  >
                    Save selection
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    data-testid={META_ADS_ACCOUNT_SELECTION_TEST_IDS.discardButton}
                    disabled={!connectionReady || !selectionDirty || selectionSaving}
                    onClick={handleDiscardSelection}
                  >
                    Discard changes
                  </Button>
                </Stack>
              </Box>
            );
          })()}

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1.4fr) repeat(2, minmax(150px, 1fr)) auto' },
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            {adAccounts.length > 0 ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="meta-ads-account-label">Ad Account</InputLabel>
                <Select
                  labelId="meta-ads-account-label"
                  label="Ad Account"
                  value={selectedAdAccountId}
                  onChange={(event) => {
                    setSelectedAdAccountId(event.target.value);
                    setManualAdAccountId('');
                    clearPreview();
                  }}
                >
                  {adAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {formatAccountLabel(account)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Ad account ID"
                size="small"
                value={manualAdAccountId}
                onChange={(event) => {
                  setManualAdAccountId(event.target.value);
                  clearPreview();
                }}
                placeholder="act_123456789"
                fullWidth
              />
            )}
            <TextField
              label="Since"
              type="date"
              size="small"
              value={since}
              onChange={(event) => {
                setSince(event.target.value);
                clearPreview();
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Until"
              type="date"
              size="small"
              value={until}
              onChange={(event) => {
                setUntil(event.target.value);
                clearPreview();
              }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              startIcon={previewLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              onClick={handlePreview}
              disabled={!canPreview}
              sx={{ minWidth: 150 }}
            >
              Preview
            </Button>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mt: 1.5 }}
          >
            <TextField
              label="Metadata name prefix"
              size="small"
              value={metadataNamePrefix}
              onChange={(event) => {
                setMetadataNamePrefix(event.target.value);
                clearPreview();
              }}
              placeholder="SEGMENTLY_API_TEST"
              sx={{ flex: 1, minWidth: { sm: 260 } }}
            />
            <Button
              variant="outlined"
              startIcon={metadataPreviewLoading ? <CircularProgress size={18} color="inherit" /> : <CampaignIcon />}
              onClick={handleMetadataPreview}
              disabled={!canPreviewMetadata}
              sx={{ minWidth: 190 }}
            >
              Preview Metadata
            </Button>
            <Button
              variant="outlined"
              startIcon={metadataSyncLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={handleSyncMetadata}
              disabled={!canSyncMetadata}
              sx={{ minWidth: 170 }}
            >
              Sync Metadata
            </Button>
            <Button
              variant="outlined"
              startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={handleSyncDelivery}
              disabled={!canSyncDelivery}
              sx={{ minWidth: 170 }}
            >
              Sync Delivery
            </Button>
          </Stack>

          {syncResult && (
            <Alert severity={syncResult.truncated ? 'warning' : 'success'} sx={{ mt: 2 }} data-testid="meta-ads-sync-delivery-result">
              Synced {syncResult.deliveryRowsWritten ?? syncResult.rowsWritten} delivery rows
              {typeof syncResult.actionRowsWritten === 'number' ? ` and ${syncResult.actionRowsWritten} action rows` : ''}
              {' '}from {syncResult.rowsRead} Meta Ads rows into {syncResult.datasetId}.{syncResult.tableId}.
              {syncResult.truncated
                ? ' Warning: the read hit the per-run page cap, so this window is INCOMPLETE — re-sync it in narrower date slices.'
                : ''}
            </Alert>
          )}

          {metadataSyncResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Synced metadata rows into {metadataSyncResult.datasetId}: {metadataSyncResult.rowsWritten.campaigns} campaigns,
              {' '}{metadataSyncResult.rowsWritten.adsets} ad sets, {metadataSyncResult.rowsWritten.ads} ads,
              {' '}and {metadataSyncResult.rowsWritten.creatives} creatives.
            </Alert>
          )}

          {isAdmin && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2">Scheduled sync</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active cron definitions for the selected ad account.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={scheduleLoading === 'metadata' ? <CircularProgress size={18} color="inherit" /> : <ScheduleIcon />}
                  onClick={handleScheduleMetadata}
                  disabled={!canScheduleSync}
                  sx={{ minWidth: 190 }}
                >
                  Daily Metadata
                </Button>
                <Button
                  variant="outlined"
                  startIcon={scheduleLoading === 'insights' ? <CircularProgress size={18} color="inherit" /> : <ScheduleIcon />}
                  onClick={handleScheduleInsights}
                  disabled={!canScheduleSync}
                  sx={{ minWidth: 190 }}
                >
                  Hourly Insights
                </Button>
              </Stack>
              {scheduleNotice && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  {scheduleNotice}
                </Alert>
              )}
              {scheduleError && (
                <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setScheduleError(null)}>
                  {scheduleError}
                </Alert>
              )}
            </Box>
          )}

          {metadataPreview && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Metadata preview
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}>
                <Chip size="small" label={`Campaigns: ${metadataPreview.counts.campaigns}`} />
                <Chip size="small" label={`Ad sets: ${metadataPreview.counts.adsets}`} />
                <Chip size="small" label={`Ads: ${metadataPreview.counts.ads}`} />
                <Chip size="small" label={`Creatives: ${metadataPreview.counts.creatives}`} />
              </Stack>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {[
                  { label: 'Campaigns', rows: metadataPreview.campaigns },
                  { label: 'Ad sets', rows: metadataPreview.adsets },
                  { label: 'Ads', rows: metadataPreview.ads },
                  { label: 'Creatives', rows: metadataPreview.creatives },
                ].map(({ label, rows }) => {
                  const previewRows = rows as Array<Record<string, unknown>>;
                  return (
                    <Box
                      key={label}
                      sx={{
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {label}: {previewRows.length}
                      </Typography>
                      {previewRows.slice(0, 3).map((row, index) => (
                        <Box key={`${label}-${index}`} sx={{ mt: 0.75 }}>
                          {formatMetadataRowLines(label, row).map((line, lineIndex) => (
                            <Typography
                              key={`${label}-${index}-${lineIndex}`}
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', wordBreak: 'break-word' }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {preview && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview rows: {preview.rows.length}
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {preview.rows.slice(0, 5).map((row, index) => (
                  <Box
                    key={`${preview.adAccountId}-${index}`}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getRowValue(row, 'campaign_name')} / {getRowValue(row, 'ad_name')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getRowValue(row, 'date_start')} | spend {getRowValue(row, 'spend')} | clicks {getRowValue(row, 'clicks')} | impressions {getRowValue(row, 'impressions')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <MetaAdsUtmMappingSection
            projectId={projectId}
            status={status}
            adAccountId={previewAdAccountId}
          />
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderColor: theme.palette.divider,
            borderStyle: 'dashed',
          }}
        >
          <CampaignIcon color="action" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Connect Meta Ads
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 620, mx: 'auto' }}
          >
            Authorize Segmently to read ad accounts and campaign insights. Tokens are stored in Google Secret Manager.
          </Typography>
          <Button
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <CampaignIcon />}
            onClick={connect}
            disabled={actionLoading || !providerConfigured}
          >
            {actionLoading ? 'Connecting...' : 'Connect Meta Ads'}
          </Button>
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
            <AlertTitle>Required Meta app setup</AlertTitle>
            <Typography variant="body2">
              The first implementation verifies OAuth, ad account listing, and insights preview before any BigQuery schema changes.
            </Typography>
          </Alert>
        </Paper>
      )}
    </Box>
  );
}

export default MetaAdsIntegrationContent;
